import { getToolName, isToolUIPart } from 'ai';
import { LIST_STRUCTURES_TOOL_NAME, MUTATING_TOOL_NAMES } from '../ops/core';
import type { AgentUIMessage } from '../contract';

type AgentPart = AgentUIMessage['parts'][number];

/**
 * Settle tool calls orphaned by an aborted turn (Stop / drawer close while
 * a plan was in flight). A dangling call would otherwise lock the server's
 * mutation gate for the rest of the conversation and fail the next provider
 * request. A mutating call settles as an explicit cancelled-failure outcome
 * (the model and the audit both see the cancellation); every read call
 * (list_structures, the PACS and job reads, image analysis, zoom) settles
 * as a tool error — a fabricated empty result would lie.
 */
export function repairDanglingToolCalls(
    messages: ReadonlyArray<AgentUIMessage>,
    skipCallIds: ReadonlySet<string> = new Set()
): {
    messages: AgentUIMessage[];
    repaired: boolean;
    repairedCallIds: string[];
} {
    const repairedCallIds: string[] = [];

    const needsRepair = (part: AgentPart): boolean =>
        isToolUIPart(part) &&
        part.state === 'input-available' &&
        !skipCallIds.has(part.toolCallId);

    const repairPart = (part: AgentPart): AgentPart => {
        if (!needsRepair(part) || !isToolUIPart(part)) {
            return part;
        }
        const toolName = getToolName(part);
        if (MUTATING_TOOL_NAMES.has(toolName)) {
            repairedCallIds.push(part.toolCallId);
            return {
                ...part,
                state: 'output-available',
                output: {
                    status: 'failed',
                    reason: 'cancelled before the clinician applied or rejected it',
                },
            } as AgentPart;
        }
        // Any read call: never left unanswered (the provider rejects a
        // conversation with a tool call that has no result).
        repairedCallIds.push(part.toolCallId);
        return {
            ...part,
            state: 'output-error',
            errorText:
                toolName === LIST_STRUCTURES_TOOL_NAME
                    ? 'cancelled before the structures were read'
                    : 'cancelled before the viewer answered',
        } as AgentPart;
    };

    const repairedMessages = messages.map((message) =>
        message.parts.some(needsRepair)
            ? { ...message, parts: message.parts.map(repairPart) }
            : message
    );

    return {
        messages: repairedMessages,
        repaired: repairedCallIds.length > 0,
        repairedCallIds,
    };
}
